import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSocketEvent from './useSocketEvent'
import type { EventType } from 'api/supportAssistant'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useChatWelcomeMessage from './useChatWelcomeMessage'
import useChatMessages from './useChatMessages'
import { useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'

// How long "Reading your message" is plausible before it becomes a stall.
const READING_LABEL_DURATION = 2500

const MESSAGE_PROGRESS_EVENT = 'message:progress' as EventType

function useChatState(): [Array<App.ChatMessage>, boolean, boolean, boolean | undefined, string | null] {
  const notLoggedIn = !useAppSelector(selectLoggedIn)
  const intl = useIntl()

  const currentChatId = useAppSelector(state => state.supportAssistant.currentChatId)
  const chatsFetching = useAppSelector(state => state.supportAssistant.chats.fetching)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const listOrders = queryParams.get('list-orders') === 'true'

  const [welcomeMessage, welcomeMessageFetching] = useChatWelcomeMessage()
  const [messages, messagesLoading] = useChatMessages(currentChatId ?? 'default')

  const [typing, setTyping] = useState<boolean>(false)
  const [agentTyping, setAgentTyping] = useState<boolean>(false)

  // What the assistant is doing right now. Transient and never persisted: it lives and dies with
  // the typing indicator, which is what keeps a late-arriving event from stranding a stale label
  // under an answer that has already rendered.
  const [progressLabel, setProgressLabel] = useState<string | null>(null)
  const currentTurnId = useRef<string | null>(null)

  const readingLabel = intl.formatMessage({
    id: 'PkzQAZzFzeIMgsFVK-n-',
    defaultMessage: 'Reading your message',
    description: 'First status shown while the support assistant starts working on a reply',
  })

  const thinkingLabel = intl.formatMessage({
    id: '9GF6JIg2culK8v1YYGM-',
    defaultMessage: 'Looking into your request',
    description: 'Status shown while the support assistant is working and has nothing more specific to report',
  })

  // Whether the label on screen is one of ours or one the server sent. Once the server speaks it
  // owns the line: the ladder below must never talk over something specific with something vague.
  const serverHasSpoken = useRef(false)
  const ladderTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // The typing event is emitted synchronously by the server, whereas the first progress event costs
  // a pubsub hop — and a greeting or a cached answer may produce no server label at all. So the
  // portal owns a short default ladder: "Reading your message" is only true for a moment, and
  // sitting on it for the length of a slow turn reads as a stall.
  const onTyping = useCallback(() => {
    currentTurnId.current = null
    serverHasSpoken.current = false
    setTyping(true)
    setProgressLabel(readingLabel)

    clearTimeout(ladderTimer.current)
    ladderTimer.current = setTimeout(() => {
      if (!serverHasSpoken.current) setProgressLabel(thinkingLabel)
    }, READING_LABEL_DURATION)
  }, [readingLabel, thinkingLabel])

  const onProgress = useCallback((payload: { turnId: string, label: string }) => {
    // A turn the customer has already moved past can still have an event in flight.
    if (currentTurnId.current && currentTurnId.current !== payload.turnId) return
    currentTurnId.current = payload.turnId
    serverHasSpoken.current = true
    clearTimeout(ladderTimer.current)
    setProgressLabel(payload.label)
  }, [])

  useSocketEvent('response:typing', onTyping)
  // The pinned contract-support predates this event, so its EventType union has no member for it.
  // Remove the assertion once the package is bumped past the svc-support change that adds it.
  useSocketEvent(MESSAGE_PROGRESS_EVENT, onProgress)
  useSocketEvent('response:agent-typing', () => setAgentTyping(true))

  const realMessages = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return []

    if (notLoggedIn && welcomeMessage) {
      if (messages.length === 0) return [welcomeMessage]

      // If the only message is the welcome message, do not inject it into the messages.
      if (messages.length === 1 && messages[0].sender !== 'chatbot') {
        return [welcomeMessage, ...messages]
      }
    }

    return messages.length === 1 && welcomeMessage && messages[0].sender !== 'chatbot' ? [welcomeMessage, ...messages] : [...messages]
  }, [messages, notLoggedIn, welcomeMessage])

  const fetching = welcomeMessageFetching || messagesLoading || realMessages.length === 0 || listOrders && chatsFetching

  useEffect(() => {
    if (realMessages.length > 1) {
      const lastMessage = realMessages[realMessages.length - 1]
      if (lastMessage.sender !== 'user') {
        setTyping(false)
        setAgentTyping(false)
        setProgressLabel(null)
        currentTurnId.current = null
        serverHasSpoken.current = false
        clearTimeout(ladderTimer.current)
      }
    }
  }, [realMessages])

  return [
    realMessages,
    typing,
    agentTyping,
    fetching,
    progressLabel,
  ]
}

export default useChatState
